import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { InfluencerDetailsService } from '../../services/influencer-details.service';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { MediaResponse } from '../../../influencer/modules/media/models/media.model';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { getApiErrorMessage } from '../../../../shared/utils/error-utils';
import { AppStateService } from '../../../../core/services/app-state.service';
import { Profile } from '../../../profile/model/profile.model';
import { Paths } from '../../../../core/constants/path.constants';
import {
  Cart,
  CartRequest,
  PurchaseType,
} from '../../../events/models/cart.model';
import {
  Subscription,
  SubscriptionRes,
} from '../../../influencer/modules/subscription/models/subscription.model';
import { CartService } from '../../../../shared/services/cart.service';
import { ApiResponse } from '../../../../shared/models/api-response.model';
import { LocalStorageService } from '../../../../core/services/local-storage.service';
import { AuthResponse } from '../../../auth/models/auth.model';
import { LocalStorageKeys } from '../../../../core/constants/local-storage.constants';

@Component({
  selector: 'vota-influencer-media',
  templateUrl: './influencer-media.component.html',
  styleUrl: './influencer-media.component.scss',
})
export class InfluencerMediaComponent implements OnInit, OnDestroy {
  username: string | null = null;
  posts$: Observable<MediaResponse[]> = of([]);
  influencerDetail$: Observable<Profile | null> = of(null);
  isLoading = false;
  userDetails: AuthResponse | undefined | null;
  userNotFound: boolean = false;
  private destroy$ = new Subject<void>();
  pageNumber: number = 1;
  nextPageNo: number = 0;
  @Input() isChildView = false;

  constructor(
    private route: ActivatedRoute,
    private influencerDetailsService: InfluencerDetailsService,
    private toastr: ToastrService,
    private appStateService: AppStateService,
    private cartService: CartService,
    private router: Router,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    if(!this.isChildView) {
    this.appStateService.setTitle('Media')};
    this.route.paramMap.subscribe((params) => {
      this.username = params.get('username') ?? '';
      this.getInfluencersDetail();
    });
  }

  getInfluencersDetail(isFirstLoad: boolean = true, pageNo: number = 1): void {
    if (!this.username) return;

    this.isLoading = true;

    this.userDetails =
      this.localStorageService.getItemWithExpiry<AuthResponse | null>(
        LocalStorageKeys.USER_DETAILS
      );

    const postsApi$ = this.userDetails
      ? this.influencerDetailsService.getInfluencerSubscribedPosts({
          userName: this.username,
          pageNumber: pageNo,
        })
      : this.influencerDetailsService.getInfluencersPosts({
          userName: this.username,
          pageNumber: pageNo,
        });

    const posts$ = postsApi$.pipe(
      tap((res) => {
        this.nextPageNo = res?.data?.nextPage ?? 1;
      }),
      map((res) => res?.data?.data ?? []),
      catchError((error: HttpErrorResponse) => {
        this.toastr.error('Failed to load posts: ' + getApiErrorMessage(error));
        return of([]);
      })
    );

    if (isFirstLoad) {
      const profile$ = this.influencerDetailsService
        .getInfluencerDetails(this.username)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(
              'Failed to load profile: ' + getApiErrorMessage(error)
            );
            return of({ data: null });
          })
        );

      forkJoin({ posts: posts$, profile: profile$ })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isLoading = false)),
          shareReplay(1)
        )
        .subscribe(({ posts, profile }) => {
          this.posts$ = of(posts).pipe(shareReplay(1));
          const userProfile = profile?.data ?? null;
          this.influencerDetail$ = of(userProfile).pipe(shareReplay(1));
          this.userNotFound = !userProfile;
        });
    } else {
      forkJoin({ posts: posts$ })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isLoading = false)),
          shareReplay(1)
        )
        .subscribe(({ posts: newPosts }) => {
          // 👇 Get previous posts
          this.posts$.pipe(take(1)).subscribe((existingPosts) => {
            const allPosts = [...existingPosts, ...newPosts];

            // 👇 Emit merged posts
            this.posts$ = of(allPosts).pipe(shareReplay(1));
          });
        });
    }
  }

  loadMore(): void {
    this.pageNumber++;
    this.getInfluencersDetail(false, this.pageNumber); // only posts
  }

  onNavigateToProfile(): void {
    this.router.navigate([ `${this.username}`]);
  }

  onPurchaseSubscription(subscription: SubscriptionRes): void {
    // this.router.navigate([Paths.TICKETS_CHECKOUT], {
    //   queryParams: { cId: cartRes.data?.id, rId: cartRes.data?.id },
    // });

    this.isLoading = true;
    const cartData: CartRequest = {
      subscriptionId: subscription.id,
      totalTickets: 1,
      totalAmount: subscription.amount,
      purchaseType: PurchaseType.Subscription,
    };
    this.cartService
      .addTicketToCards(cartData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (cartRes: ApiResponse<Cart>) => {
          this.router.navigate([Paths.TICKETS_CHECKOUT], {
            queryParams: {
              cId: cartRes.data?.id,
              sId: cartRes.data?.subscriptionId,
            },
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error(getApiErrorMessage(error));
        },
      });
  }

  onBackToHome(): void {
    this.router.navigate([Paths.EMPTY]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
